import { useState, useEffect, useCallback } from 'react';
import { useBlocker } from 'react-router-dom';
import {
  DRAFT_PREFIX, getDraftItem, setDraftItem, removeDraftItem, listDraftKeys,
} from '../utils/draftStorage';
import { logger } from '../utils/logger';

const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/** Remove all expired drafts on module load. Drafts now live in
 * sessionStorage so they auto-expire on tab close — TTL is a secondary
 * cleanup for very long-running tab sessions.
 */
function purgeExpiredDrafts() {
  try {
    const now = Date.now();
    listDraftKeys().forEach((key) => {
      const raw = getDraftItem(key);
      if (!raw) return;
      try {
        const { savedAt } = JSON.parse(raw);
        if (savedAt && now - new Date(savedAt).getTime() > DRAFT_TTL_MS) {
          removeDraftItem(key);
        }
      } catch (err) {
        logger.error(`Corrupted draft entry "${key}", removing:`, err);
        removeDraftItem(key);
      }
    });
  } catch (err) {
    logger.error('Draft purge failed (storage unavailable):', err);
  }
}

// Run once on module load.
if (typeof window !== 'undefined') purgeExpiredDrafts();

/**
 * Read a draft, returning null if expired or missing.
 */
function readDraft(storageKey) {
  try {
    const raw = getDraftItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.savedAt && Date.now() - new Date(parsed.savedAt).getTime() > DRAFT_TTL_MS) {
      removeDraftItem(storageKey);
      return null;
    }
    return parsed;
  } catch (err) {
    logger.error(`Failed to read draft "${storageKey}":`, err);
    return null;
  }
}

/**
 * Hook to guard forms against accidental data loss, with optional draft persistence.
 *
 * Drafts are now stored in sessionStorage (not localStorage) so business-
 * content drafts (task titles, payment notes, etc.) auto-clear when the
 * browser tab closes — critical for shared-workstation privacy.
 *
 * @param {boolean} isDirty - Whether the form has unsaved changes
 * @param {object} options
 * @param {string} [options.draftKey] - draft key suffix for persistence
 * @param {function} [options.getFormData] - Returns current form data for draft saving
 * @param {function} [options.setFormData] - Restores form data from draft
 */
export function useFormGuard(isDirty, options = {}) {
  const { draftKey, getFormData, setFormData } = options;
  const storageKey = draftKey ? `${DRAFT_PREFIX}${draftKey}` : null;

  const [pendingAction, setPendingAction] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);

  // Check for existing non-expired draft on mount
  useEffect(() => {
    if (!storageKey) return;
    setHasDraft(readDraft(storageKey) !== null);
  }, [storageKey]);

  // Browser tab close / hard refresh — auto-save draft
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
      if (storageKey && getFormData) {
        try {
          const data = getFormData();
          setDraftItem(storageKey, JSON.stringify({ data, savedAt: new Date().toISOString() }));
        } catch (err) {
          logger.error('Draft auto-save on beforeunload failed:', err);
        }
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, storageKey, getFormData]);

  // Block route navigation when dirty (works with createBrowserRouter)
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    isDirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      // Surface the dialog: queue the navigation proceed as the pending action
      setPendingAction(() => () => blocker.proceed?.());
    }
  }, [blocker]);

  const guard = useCallback((action) => {
    if (isDirty) {
      setPendingAction(() => action);
      return true;
    }
    action();
    return false;
  }, [isDirty]);

  const discard = useCallback(() => {
    const action = pendingAction;
    setPendingAction(null);
    action?.();
  }, [pendingAction]);

  const cancel = useCallback(() => {
    setPendingAction(null);
    if (blocker?.state === 'blocked') blocker.reset?.();
  }, [blocker]);

  const save = useCallback(async (saveFn) => {
    const action = pendingAction;
    setPendingAction(null);
    if (saveFn) {
      try { await saveFn(); } catch (err) { logger.error('Save failed:', err); return; }
    }
    if (storageKey) {
      removeDraftItem(storageKey);
      setHasDraft(false);
    }
    action?.();
  }, [pendingAction, storageKey]);

  const saveDraft = useCallback(() => {
    if (storageKey && getFormData) {
      try {
        const data = getFormData();
        setDraftItem(storageKey, JSON.stringify({ data, savedAt: new Date().toISOString() }));
        setHasDraft(true);
      } catch (err) {
        logger.error('Draft save failed:', err);
      }
    }
    const action = pendingAction;
    setPendingAction(null);
    action?.();
  }, [storageKey, getFormData, pendingAction]);

  const restoreDraft = useCallback(() => {
    if (!storageKey || !setFormData) return false;
    const parsed = readDraft(storageKey);
    if (!parsed) return false;
    setFormData(parsed.data);
    return true;
  }, [storageKey, setFormData]);

  const clearDraft = useCallback(() => {
    if (!storageKey) return;
    removeDraftItem(storageKey);
    setHasDraft(false);
  }, [storageKey]);

  return {
    showDialog: pendingAction !== null,
    guard,
    discard,
    cancel,
    save,
    saveDraft: storageKey ? saveDraft : null,
    hasDraft,
    restoreDraft,
    clearDraft,
  };
}
